import ParameterComponentTag from './ParameterComponentTag';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Collapse, Drawer, Empty, Space, Spin, Table, Tag, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/tr';
import { getReportFormatWeeklyStatus } from '../services/report-formatService';
import { ReportFormatMonthlyStatus } from '../types/ReportFormat';

dayjs.extend(isoWeek);
const today = () => dayjs().locale('tr');
const key = (date: Dayjs) => date.format('YYYY-MM-DD');
const status = (value?: boolean | null) => <Tag color={value === true ? 'green' : 'red'}>{value === true ? 'Evet' : value == null ? 'Yok' : 'Hayır'}</Tag>;
export function groupWeeklyPlans(data: ReportFormatMonthlyStatus[], week: Dayjs) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = week.add(index, 'day');
    const rows = data.map(row => ({ ...row, inspections: (row.inspections || []).filter(item => item.date === key(date)) }))
      .filter(row => row.inspections.length > 0)
      .sort((a, b) => b.inspections.length - a.inspections.length || (a.name || '').localeCompare(b.name || '', 'tr'));
    return { date, rows };
  });
}
interface InspectorPlanRow extends ReportFormatMonthlyStatus {
  inspectorKey: string;
  inspectorName: string;
  inspectorTaskCount: number;
  proposals: Array<{ id: number; number: string }>;
}

export function groupInspectorPlans(row: ReportFormatMonthlyStatus): InspectorPlanRow[] {
  const groups = new Map<string, InspectorPlanRow>();
  for (const inspection of row.inspections) {
    const inspectorKey = inspection.accountId == null ? 'unassigned' : String(inspection.accountId);
    const proposalNumber = inspection.proposalNumber?.trim();
    const proposal = inspection.proposalId != null && proposalNumber
      ? { id: inspection.proposalId, number: proposalNumber } : null;
    const existing = groups.get(inspectorKey);
    if (existing) {
      existing.inspectorTaskCount += 1;
      if (proposal && !existing.proposals.some(item => item.id === proposal.id)) {
        existing.proposals.push(proposal);
      }
      continue;
    }
    const fullName = [inspection.firstName, inspection.lastName].map(value => value?.trim()).filter(Boolean).join(' ');
    const inspectorName = inspection.accountId == null ? 'Uzman Atanmamış'
      : fullName || inspection.email?.trim() || `Uzman #${inspection.accountId}`;
    groups.set(inspectorKey, { ...row, inspectorKey, inspectorName, inspectorTaskCount: 1, proposals: proposal ? [proposal] : [] });
  }
  for (const group of Array.from(groups.values())) {
    group.proposals.sort((a, b) => a.number.localeCompare(b.number, 'tr', { numeric: true }) || a.id - b.id);
  }
  return Array.from(groups.values()).sort((a, b) => b.inspectorTaskCount - a.inspectorTaskCount
    || a.inspectorName.localeCompare(b.inspectorName, 'tr') || a.inspectorKey.localeCompare(b.inspectorKey));
}

const columns = [
  {
    title: 'Teklif Numarası', key: 'proposals',
    render: (_: unknown, row: InspectorPlanRow) => row.proposals.length ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {row.proposals.map(proposal => (
          <Typography.Link key={proposal.id} href={`/proposals?search=${encodeURIComponent(proposal.number)}`}>
            {proposal.number}
          </Typography.Link>
        ))}
      </div>
    ) : '-',
  },
  { title: 'Ad Soyad', dataIndex: 'inspectorName' },
  { title: 'Görev Sayısı', dataIndex: 'inspectorTaskCount' },
  { title: 'Rapor Formatı', dataIndex: 'hasReportFormat', render: status },
  { title: 'Aktif', key: 'active', render: (_: unknown, row: ReportFormatMonthlyStatus) => row.hasReportFormat === true ? status(row.isActive) : '-' },
  { title: 'Test Edildi', key: 'tested', render: (_: unknown, row: ReportFormatMonthlyStatus) => row.hasReportFormat === true && row.isActive === true ? status(row.isTested) : '-' },
];
export default function WeeklyPlanDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [week, setWeek] = useState(() => today().startOf('isoWeek'));
  const [activeDay, setActiveDay] = useState<string | string[]>(key(today()));
  const [data, setData] = useState<ReportFormatMonthlyStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (open) { setWeek(today().startOf('isoWeek')); setActiveDay(key(today())); }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setData([]); setLoading(true); setError(false);
    getReportFormatWeeklyStatus(key(week))
      .then(response => { if (!cancelled) setData(response || []); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, week]);
  const changeWeek = (next: Dayjs) => {
    setWeek(next);
    setActiveDay(key(next.isSame(today(), 'isoWeek') ? today() : next));
  };
  return <Drawer title='Haftalık Plan' open={open} onClose={onClose} width='min(1000px, 100vw)' destroyOnClose>
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      <Space wrap>
        <Button onClick={() => changeWeek(week.subtract(1, 'week'))}>Önceki Hafta</Button>
        <Button onClick={() => changeWeek(today().startOf('isoWeek'))}>Bu Hafta</Button>
        <Button onClick={() => changeWeek(week.add(1, 'week'))}>Sonraki Hafta</Button>
        <Typography.Text strong>{week.format('DD.MM.YYYY')} – {week.add(6, 'day').format('DD.MM.YYYY')}</Typography.Text>
      </Space>
      {error ? <Alert type='error' showIcon message='Haftalık plan yüklenemedi. Lütfen haftayı yeniden seçin.' /> : <Spin spinning={loading}>
        <Collapse accordion activeKey={activeDay} onChange={setActiveDay} items={groupWeeklyPlans(data, week).map(({ date, rows }) => ({
          key: key(date), label: date.format('DD.MM.YYYY dddd'),
          children: rows.length ? rows.map(row => <section key={row.attributeId} style={{ marginBottom: 20 }}>
            <Typography.Title level={5} style={{ fontWeight: 700 }}>{row.name || '-'}<ParameterComponentTag toolId={row.toolId} toolName={row.toolName} /></Typography.Title>
            <Table size='small' rowKey='inspectorKey' columns={columns} dataSource={groupInspectorPlans(row)} pagination={false} scroll={{ x: 840 }} />
          </section>) : <Empty description='Bu gün için plan bulunamadı.' />,
        }))} />
      </Spin>}
    </Space>
  </Drawer>;
}
